import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { auth, db } from './firebase';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import FortnoxCallback from './components/FortnoxCallback';
import BCCallback from './components/BCCallback';
import D365Callback from './components/D365Callback';
import VismaCallback from './components/VismaCallback';
import BokioCallback from './components/BokioCallback';
import XeroCallback from './components/XeroCallback';
import SageCallback from './components/SageCallback';
import SpirisCallback from './components/SpirisCallback';
import MainLayout from './components/layout/MainLayout';
import RequireInvoiceModule from './components/RequireInvoiceModule';
import RequireCurrencyModule from './components/RequireCurrencyModule';
import OrgHomeRedirect from './components/OrgHomeRedirect';
import { validateInvitation, markInvitationAsUsed } from './services/invitationService';
import {
  beginSmsSignInChallenge,
  completeSmsSignIn,
  resendSmsSignInChallenge,
} from './services/mfaPhoneAuth';
import { formatErrorMessage, logError } from './utils/errorHandler';
import { DEFAULT_LOGO_URL } from './constants';
import './App.css';

// Code splitting - lazy load pages for smaller initial bundle
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CurrencyExposure = lazy(() => import('./pages/CurrencyExposure'));
const ScannedInvoices = lazy(() => import('./pages/ScannedInvoices'));
const ScannedInvoiceDetail = lazy(() => import('./pages/ScannedInvoiceDetail'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const Customers = lazy(() => import('./pages/Customers'));
const CounterpartyScreening = lazy(() => import('./pages/CounterpartyScreening'));
const Integrations = lazy(() => import('./pages/Integrations'));
const FileUpload = lazy(() => import('./pages/FileUpload'));
const Settings = lazy(() => import('./pages/Settings'));
const KnowledgeHub = lazy(() => import('./pages/KnowledgeHub'));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

function LoginView() {
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [invitationError, setInvitationError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(null);
  const [mfaSmsCode, setMfaSmsCode] = useState('');
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const mfaResolverRef = useRef(null);
  const mfaHintRef = useRef(null);
  const mfaVerifierRef = useRef(null);
  /** When Google sign-in hits MFA before invitation is consumed, finish onboarding after SMS step. */
  const pendingGoogleInvitationRef = useRef(null);

  const enterMfaFlow = async (multiFactorError) => {
    try {
      const { resolver, hint, verifier, verificationId } = await beginSmsSignInChallenge(
        auth,
        multiFactorError,
        'recaptcha-mfa-signin'
      );
      mfaResolverRef.current = resolver;
      mfaHintRef.current = hint;
      mfaVerifierRef.current = verifier;
      setMfaVerificationId(verificationId);
      setSignInEmail((prev) => auth.currentUser?.email || prev);
      setMfaStep('needCode');
    } catch (mfaErr) {
      alert(formatErrorMessage(mfaErr));
      resetMfaFlow();
    }
  };

  const resetMfaFlow = () => {
    if (mfaVerifierRef.current) {
      try {
        mfaVerifierRef.current.clear();
      } catch (_) {
        /* ignore */
      }
      mfaVerifierRef.current = null;
    }
    mfaResolverRef.current = null;
    mfaHintRef.current = null;
    setMfaVerificationId(null);
    setMfaSmsCode('');
    setMfaStep(null);
    pendingGoogleInvitationRef.current = null;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setInvitationToken(token);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setInvitationError(null);
    setLoading(true);
    try {
      let validInvitation = null;
      if (invitationToken) {
        try {
          const validationResult = await validateInvitation({ token: invitationToken });
          validInvitation = validationResult.data;
          if (validInvitation.email.toLowerCase() !== signUpEmail.toLowerCase()) {
            setInvitationError(`This invitation is for ${validInvitation.email}. Please use that email address.`);
            setLoading(false);
            return;
          }
        } catch (error) {
          setInvitationError(formatErrorMessage(error));
          setLoading(false);
          return;
        }
      } else {
        setInvitationError('An invitation is required to create an account. Please use the invitation link sent to your email.');
        setLoading(false);
        return;
      }
      const userCredential = await createUserWithEmailAndPassword(auth, signUpEmail, signUpPassword);
      if (validInvitation) {
        try {
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: userCredential.user.email,
            organizationId: validInvitation.organizationId || null,
            role: validInvitation.role || 'member',
            createdAt: serverTimestamp(),
          }, { merge: true });
        } catch (userDocError) {
          console.error('Error creating user profile:', userDocError);
          logError(userDocError, { context: 'createUserProfile', userId: userCredential.user.uid });
        }
      }
      if (validInvitation && invitationToken) {
        try {
          await markInvitationAsUsed({ token: invitationToken });
        } catch (error) {
          logError(error, { context: 'markInvitationAsUsed', userId: userCredential.user.uid });
        }
      }
      setSignUpEmail('');
      setSignUpPassword('');
      setInvitationToken('');
      setInvitationError(null);
    } catch (error) {
      setInvitationError(formatErrorMessage(error));
      logError(error, { context: 'signup' });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, signInEmail, signInPassword);
      resetMfaFlow();
      setSignInEmail('');
      setSignInPassword('');
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        await enterMfaFlow(error);
      } else {
        alert(formatErrorMessage(error));
      }
    }
    setLoading(false);
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    const resolver = mfaResolverRef.current;
    const verifier = mfaVerifierRef.current;
    const hint = mfaHintRef.current;
    if (!resolver || !verifier || !mfaVerificationId) {
      alert('MFA session expired. Please sign in again.');
      resetMfaFlow();
      return;
    }
    setLoading(true);
    try {
      await completeSmsSignIn(resolver, verifier, mfaVerificationId, mfaSmsCode);
      mfaVerifierRef.current = null;
      mfaResolverRef.current = null;
      mfaHintRef.current = null;
      setMfaVerificationId(null);
      setMfaSmsCode('');
      setMfaStep(null);
      setSignInEmail('');
      setSignInPassword('');

      const pending = pendingGoogleInvitationRef.current;
      if (pending?.token && pending?.invitation) {
        pendingGoogleInvitationRef.current = null;
        const u = auth.currentUser;
        if (!u?.email || u.email.toLowerCase() !== pending.invitation.email.toLowerCase()) {
          await signOut(auth);
          alert(`This invitation is for ${pending.invitation.email}. Sign in with that account.`);
          setLoading(false);
          return;
        }
        try {
          await markInvitationAsUsed({ token: pending.token });
          await setDoc(
            doc(db, 'users', u.uid),
            { email: u.email, createdAt: serverTimestamp() },
            { merge: true }
          );
          setInvitationToken('');
          setInvitationError(null);
          setSignUpEmail('');
          setSignUpPassword('');
        } catch (invErr) {
          await signOut(auth);
          alert(formatErrorMessage(invErr));
          logError(invErr, { context: 'markInvitationAsUsedGoogleAfterMfa', userId: u.uid });
        }
      }
    } catch (err) {
      alert(formatErrorMessage(err));
      try {
        const { verifier: newVerifier, verificationId: newVid } = await resendSmsSignInChallenge(
          auth,
          resolver,
          hint,
          'recaptcha-mfa-signin',
          null
        );
        mfaVerifierRef.current = newVerifier;
        setMfaVerificationId(newVid);
        setMfaSmsCode('');
      } catch {
        resetMfaFlow();
        alert('Could not send a new verification code. Please try signing in again.');
      }
    }
    setLoading(false);
  };

  const handleMfaResend = async () => {
    const resolver = mfaResolverRef.current;
    const hint = mfaHintRef.current;
    if (!resolver || !hint) return;
    setLoading(true);
    try {
      const { verifier: newVerifier, verificationId: newVid } = await resendSmsSignInChallenge(
        auth,
        resolver,
        hint,
        'recaptcha-mfa-signin',
        mfaVerifierRef.current
      );
      mfaVerifierRef.current = newVerifier;
      setMfaVerificationId(newVid);
      setMfaSmsCode('');
    } catch (err) {
      alert(formatErrorMessage(err));
      resetMfaFlow();
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setInvitationError(null);
    pendingGoogleInvitationRef.current = null;
    setLoading(true);
    try {
      let validInvitation = null;
      if (invitationToken) {
        try {
          const validationResult = await validateInvitation({ token: invitationToken });
          validInvitation = validationResult.data;
          pendingGoogleInvitationRef.current = {
            token: invitationToken,
            invitation: validInvitation,
          };
        } catch (error) {
          setInvitationError(formatErrorMessage(error));
          setLoading(false);
          return;
        }
      }

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      let credUser;
      try {
        const credential = await signInWithPopup(auth, provider);
        credUser = credential.user;
      } catch (error) {
        if (error.code === 'auth/multi-factor-auth-required') {
          await enterMfaFlow(error);
          setLoading(false);
          return;
        }
        pendingGoogleInvitationRef.current = null;
        throw error;
      }

      if (invitationToken && validInvitation) {
        pendingGoogleInvitationRef.current = null;
        if (!credUser.email || credUser.email.toLowerCase() !== validInvitation.email.toLowerCase()) {
          await signOut(auth);
          setInvitationError(
            `This invitation is for ${validInvitation.email}. Choose that Google account or use email signup below.`
          );
          setLoading(false);
          return;
        }
        try {
          await markInvitationAsUsed({ token: invitationToken });
        } catch (error) {
          await signOut(auth);
          setInvitationError(formatErrorMessage(error));
          logError(error, { context: 'markInvitationAsUsedGoogle', userId: credUser.uid });
          setLoading(false);
          return;
        }
        try {
          await setDoc(
            doc(db, 'users', credUser.uid),
            {
              email: credUser.email,
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );
        } catch (userDocError) {
          console.error('Error updating user profile:', userDocError);
          logError(userDocError, { context: 'googleSignupProfile', userId: credUser.uid });
        }
        setInvitationToken('');
        setInvitationError(null);
        setSignUpEmail('');
        setSignUpPassword('');
      } else {
        const userSnap = await getDoc(doc(db, 'users', credUser.uid));
        const userData = userSnap.exists() ? userSnap.data() : {};
        if (!userData.organizationId) {
          await signOut(auth);
          alert(
            'This Google account is not invited to Svenry yet. Open the invitation link from your email, then sign in with Google or create a password.'
          );
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      pendingGoogleInvitationRef.current = null;
      if (invitationToken) {
        setInvitationError(formatErrorMessage(error));
      } else {
        alert(formatErrorMessage(error));
      }
      logError(error, { context: 'googleSignIn' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!signInEmail) {
      alert('Please enter your email address in the Email field first.');
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, signInEmail);
      alert('If an account exists for this email, a password reset link has been sent.');
    } catch (error) {
      alert(formatErrorMessage(error));
      logError(error, { context: 'passwordReset', email: signInEmail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6">
        <img src={DEFAULT_LOGO_URL} alt="Svenry" className="h-12 w-auto mx-auto mb-6" />
        <h2 className="text-xl font-semibold text-gray-900 text-center mb-4">Welcome to Svenry</h2>
        <div id="recaptcha-mfa-signin" />

        {mfaStep === 'needCode' ? (
          <form onSubmit={handleMfaVerify} className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Sign-in verification</h3>
            <p className="text-sm text-gray-600">
              Enter the verification code sent to your phone ({signInEmail}).
            </p>
            <input
              type="text"
              inputMode="numeric"
              placeholder="SMS code"
              value={mfaSmsCode}
              onChange={(e) => setMfaSmsCode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoComplete="one-time-code"
              disabled={loading}
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handleMfaResend}
              className="w-full text-sm text-primary-600 hover:text-primary-700"
            >
              Resend SMS code
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => resetMfaFlow()}
              className="w-full text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel and use a different account
            </button>
          </form>
        ) : (
          <form onSubmit={handleSignIn} className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Sign In</h3>
            <input
              type="email"
              placeholder="Email"
              autoComplete="username"
              value={signInEmail}
              onChange={(e) => setSignInEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              disabled={loading}
            />
            <input
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={signInPassword}
              onChange={(e) => setSignInPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={handlePasswordReset}
              className="w-full text-sm text-primary-600 hover:text-primary-700"
            >
              Forgot password?
            </button>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wide">
                <span className="bg-white px-2 text-gray-500">or</span>
              </div>
            </div>
            <button
              type="button"
              disabled={loading}
              onClick={handleGoogleSignIn}
              className="w-full py-2 px-3 border border-gray-300 rounded-md bg-white text-gray-800 font-medium text-sm hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>
          </form>
        )}
        {invitationToken && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="mb-3 p-2 bg-primary-50 text-primary-800 text-sm rounded">
              ✓ Invitation link detected. Use the email from your invitation.
            </div>
            {invitationError && (
              <div className="mb-3 p-2 bg-red-50 text-red-700 text-sm rounded">{invitationError}</div>
            )}
            <form onSubmit={handleSignUp} className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Create Account</h3>
              <input
                type="email"
                placeholder="Email"
                autoComplete="username"
                value={signUpEmail}
                onChange={(e) => setSignUpEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                required
                disabled={loading}
              />
              <input
                type="password"
                placeholder="Password"
                autoComplete="new-password"
                value={signUpPassword}
                onChange={(e) => setSignUpPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                required
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 bg-gray-800 text-white font-medium rounded-md hover:bg-gray-900 disabled:opacity-50"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-wide">
                  <span className="bg-white px-2 text-gray-500">or</span>
                </div>
              </div>
              <button
                type="button"
                disabled={loading}
                onClick={handleGoogleSignIn}
                className="w-full py-2 px-3 border border-gray-300 rounded-md bg-white text-gray-800 font-medium text-sm hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google (same email)
              </button>
              <p className="text-xs text-gray-500">
                Use the Google account that matches the email on your invitation.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (location.pathname === '/fortnox/callback') {
    return <FortnoxCallback />;
  }
  if (location.pathname === '/bc/callback') {
    return <BCCallback />;
  }
  if (location.pathname === '/d365/callback') {
    return <D365Callback />;
  }
  if (location.pathname === '/visma/callback') {
    return <VismaCallback />;
  }
  if (location.pathname === '/bokio/callback') {
    return <BokioCallback />;
  }
  if (location.pathname === '/xero/callback') {
    return <XeroCallback />;
  }
  if (location.pathname === '/sage/callback') {
    return <SageCallback />;
  }
  if (location.pathname === '/spiris/callback') {
    return <SpirisCallback />;
  }

  if (!user) {
    return <LoginView />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<OrgHomeRedirect />} />
          <Route
            path="dashboard"
            element={
              <RequireInvoiceModule>
                <Dashboard />
              </RequireInvoiceModule>
            }
          />
          <Route
            path="currency-exposure"
            element={
              <RequireInvoiceModule>
                <RequireCurrencyModule>
                  <CurrencyExposure />
                </RequireCurrencyModule>
              </RequireInvoiceModule>
            }
          />
          <Route
            path="invoices"
            element={
              <RequireInvoiceModule>
                <ScannedInvoices />
              </RequireInvoiceModule>
            }
          />
          <Route
            path="invoices/detail"
            element={
              <RequireInvoiceModule>
                <ScannedInvoiceDetail />
              </RequireInvoiceModule>
            }
          />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="customers" element={<Customers />} />
          <Route path="counterparty-screening" element={<CounterpartyScreening />} />
          <Route path="integrations" element={<Integrations />} />
          <Route
            path="upload"
            element={
              <RequireInvoiceModule>
                <FileUpload />
              </RequireInvoiceModule>
            }
          />
          <Route path="knowledge-hub" element={<KnowledgeHub />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<OrgHomeRedirect />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
